import React, { useEffect, useState, useReducer, useMemo } from "react";
import { useNavigate } from "react-router";
import HeadingBanner from "../components/HeadingBanner";
import GradeReports from "../components/GradeReports";

import {
  whoami,
  getClasses,
  getTeachers,
  getSchools,
  getAllAssessmentInstances,
  postResultsByStudent,
} from "../services/APIClient";
import { Container, Row, Col, Card, Alert } from "react-bootstrap";
import LeftPanel from "../components/LeftPanel";

import "../components/ClassCard.css";
import "../components/MasterDashCards.css";
import "../components/SuperSecretInternalDash.css";

import { exportToExcel } from "../services/ExcelExporter";
import secondsToTime from "../services/secondsToTime";

import AssessmentExport from "../components/AssessmentExport";
import GradeLevelExport from "../components/GradeLevelExport";

// Reducer for state management
const reportsReducer = (state, action) => {
  switch (action.type) {
    case 'SET_USER_DATA':
      return { ...state, ...action.payload };
    case 'SET_SCHOOL_DETAILS':
      return { ...state, schoolDetails: action.payload };
    case 'SET_ASSESSMENT_DATA':
      return { 
        ...state, 
        allAssessmentInstances: action.payload.instances,
        assessmentResultsByStudent: action.payload.results,
        isDataReadyForExport: true
      };
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    case 'SET_ERROR':
      return { ...state, errorMessage: action.payload };
    case 'SET_NOT_FOUND':
      return { ...state, notFoundMessage: action.payload };
    case 'CLEAR_MESSAGES':
      return { ...state, errorMessage: "", notFoundMessage: "" };
    default:
      return state;
  }
};

const initialState = {
  schoolid: null,
  teacherid: null,
  name: "",
  teacherEmail: null,
  schoolDetails: null,
  allAssessmentInstances: [],
  assessmentResultsByStudent: [],
  isDataReadyForExport: false,
  isLoading: false,
  errorMessage: "",
  notFoundMessage: "",
};

function Reports() {
  const [state, dispatch] = useReducer(reportsReducer, initialState);
  const [inputRollNumber, setInputRollNumber] = useState("");
  
  const navigate = useNavigate();

  // Initialize user data
  useEffect(() => {
    dispatch({ type: 'SET_LOADING', payload: true });
    whoami().then((response) => {
      const { schoolid, id, name, email } = response.data.stakeholder;
      dispatch({ 
        type: 'SET_USER_DATA', 
        payload: { schoolid, teacherid: id, name, teacherEmail: email }
      });
      dispatch({ type: 'SET_LOADING', payload: false });
    }).catch(error => {
      dispatch({ type: 'SET_ERROR', payload: "Failed to load user data" });
      dispatch({ type: 'SET_LOADING', payload: false });
    });
  }, []);

  // Memoized merged data for school-specific exports
  const mergedData = useMemo(() => {
    if (!state.allAssessmentInstances || !state.assessmentResultsByStudent) return [];
    
    const assessmentInstanceMap = state.allAssessmentInstances.reduce(
      (map, instance) => {
        map[instance.id] = instance;
        return map;
      },
      {}
    );

    return state.assessmentResultsByStudent.map((result) => {
      const instance = assessmentInstanceMap[result.assessmentInstance];
      return { ...result, ...instance };
    });
  }, [state.allAssessmentInstances, state.assessmentResultsByStudent]);

  const handleRollNumberSubmit = async () => {
    try {
      dispatch({ type: 'CLEAR_MESSAGES' });
      const schoolsResponse = await getSchools();
      const school = schoolsResponse.data.find(
        (s) => s.rollnumber === inputRollNumber.toUpperCase()
      );
      
      if (school) {
        dispatch({ type: 'SET_SCHOOL_DETAILS', payload: school });
      } else {
        dispatch({ 
          type: 'SET_NOT_FOUND', 
          payload: "No school found with the provided roll number." 
        });
      }
    } catch (error) {
      dispatch({ 
        type: 'SET_ERROR', 
        payload: "An error occurred while fetching school details: " + error.message 
      });
    }
  };

  // Load assessment data when school is selected
  useEffect(() => {
    if (!state.schoolDetails?.rollnumber) return;

    getAllAssessmentInstances(state.schoolDetails.rollnumber)
      .then((response) => {
        const instances = Array.isArray(response) ? response : (response.data || []);
        
        if (!Array.isArray(instances)) {
          throw new Error("Assessment instances response is not an array");
        }

        const instanceIds = instances.map((instance) => instance.id);
        return Promise.all([
          Promise.resolve(instances),
          postResultsByStudent(instanceIds)
        ]);
      })
      .then(([instances, studentResults]) => {
        dispatch({ 
          type: 'SET_ASSESSMENT_DATA', 
          payload: { instances, results: studentResults }
        });
      })
      .catch((error) => {
        console.error("Assessment data fetch error:", error);
        dispatch({ 
          type: 'SET_ERROR', 
          payload: "Error fetching assessment data: " + error.message 
        });
      });
  }, [state.schoolDetails]);

  const handleAssessmentReportsNav = () => {
    navigate("/assessmentreports", {
      state: {
        teachers: { teachers: [] },
        teacherid: { teacherid: state.teacherid },
        schoolid: { schoolid: state.schoolid },
      },
    });
  };

  return (
    <div>
      <Container fluid className="vh-100">
        <HeadingBanner
          name={`Welcome ${state.name}!`}
          isLoggedIn={true}
          cloud={true}
          teacherid={state.teacherid}
        />
        <Row className="h-100">
          <LeftPanel />
          <Col sm={{ span: 10, offset: 2 }} className="blue-background">
            <div className="p-4">
              {/* Search Section */}
              <Card className="mb-4">
                <Card.Header>
                  <h4>School Reports Dashboard</h4>
                </Card.Header>
                <Card.Body>
                  <Row>
                    <Col md={8}>
                      <div className="input-group mb-3">
                        <input
                          type="text"
                          className="form-control"
                          value={inputRollNumber}
                          onChange={(e) =>
                            setInputRollNumber(e.target.value.toUpperCase())
                          }
                          placeholder="Enter School Roll Number"
                        />
                        <button
                          className="btn btn-warning"
                          onClick={handleRollNumberSubmit}
                        >
                          Search School
                        </button>
                      </div>
                    </Col>
                    <Col md={4}>
                      <button
                        className="btn btn-primary w-100"
                        onClick={handleAssessmentReportsNav}
                      >
                        Assessment Reports
                      </button>
                    </Col>
                  </Row>

                  {state.notFoundMessage && (
                    <Alert variant="warning">{state.notFoundMessage}</Alert>
                  )}
                  {state.errorMessage && (
                    <Alert variant="danger">{state.errorMessage}</Alert>
                  )}
                </Card.Body>
              </Card>

              {/* Global Reports - Grade Reports Component */}
              <Card className="mb-4">
                <Card.Header>
                  <h4>Global Reports by Grade</h4>
                </Card.Header>
                <Card.Body>
                  <GradeReports />
                </Card.Body>
              </Card>

              {/* Loading State */}
              {state.isLoading && (
                <div className="text-center p-4">
                  <div className="spinner-border" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="mt-2">Loading school data...</p>
                </div>
              )}

              {/* School-Specific Reports - Only show when school selected */}
              {state.schoolDetails && state.isDataReadyForExport && (
                <div>
                  <Card className="mb-4 bg-light">
                    <Card.Body>
                      <h5>School-Specific Data</h5>
                      <p><strong>Name:</strong> {state.schoolDetails.name}</p>
                      <p><strong>Roll Number:</strong> {state.schoolDetails.rollnumber}</p>
                      <p><strong>Area:</strong> {state.schoolDetails.area}</p>
                    </Card.Body>
                  </Card>

                  <Row>
                    <Col md={6}>
                      <AssessmentExport
                        schoolDetails={state.schoolDetails}
                        assessmentResultsByStudent={state.assessmentResultsByStudent}
                        allAssessmentInstances={state.allAssessmentInstances}
                        mergedData={mergedData}
                        assessmentInstanceMap={state.allAssessmentInstances?.reduce(
                          (map, instance) => {
                            map[instance.id] = instance;
                            return map;
                          },
                          {}
                        )}
                      />
                    </Col>
                    <Col md={6}>
                      <GradeLevelExport
                        schoolDetails={state.schoolDetails}
                        assessmentResultsByStudent={state.assessmentResultsByStudent}
                        allAssessmentInstances={state.allAssessmentInstances}
                        mergedData={mergedData}
                        assessmentInstanceMap={state.allAssessmentInstances?.reduce(
                          (map, instance) => {
                            map[instance.id] = instance;
                            return map;
                          },
                          {}
                        )}
                      />
                    </Col>
                  </Row>
                </div>
              )}
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
}

export default Reports;